import Color from '@/Enums/Color.enum';
import FileStatus from '@/Enums/FirstPartyData/FileStatus.enum';
import { UsageIntent } from '@/Enums/FirstPartyData/UsageIntent.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import type { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import type { IClient } from '@/Interfaces/Client.interface';
import StatusChip from '@/components/StatusChip';
import { defaultQuickFilter } from '@/helpers/grid/quickFilter';
import { dateToFormat, dateToShortFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import { BlockOutlined, CheckCircleOutline, CloudDoneOutlined, HourglassEmptyOutlined } from '@mui/icons-material';
import { type ChipProps, Tooltip } from '@mui/material';
import { type GridRenderCellParams, getGridStringQuickFilterFn } from '@mui/x-data-grid-pro';
import type { Moment } from 'moment';

/**
 * Status tracks whether a file has been processed into the attribution dataset.
 * It does NOT track the upload, which has already succeeded for every row in
 * this grid — the file lands in storage on submit, and an hourly job then looks
 * for it downstream, giving up after 48 hours.
 *
 * Those two things being conflated is what #4395 reported: clients saw "Failed"
 * against files they had just watched upload successfully, and read it as the
 * upload breaking. Three separate problems fed that:
 *
 *  1. "Failed" names no subject, so the reader supplies one — the thing they
 *     just did.
 *  2. Uploaded / Processing / Data Ready all rendered the same green with the
 *     same tick, so an in-flight file was indistinguishable from a finished one
 *     and only the red chip stood out.
 *  3. Nothing on the page said what Status measured.
 *
 * So each state now carries its own colour, icon and explanation, and the
 * column header says what it tracks. The underlying statuses are unchanged.
 *
 * The ~30% of files that reach the failed state do so because they genuinely
 * never arrive downstream — a vendor-side gap, escalated separately on #4395,
 * not something this grid can fix. What it can do is stop implying the client
 * did something wrong.
 */
export const statusChipPropMap: Record<FileStatus, Partial<ChipProps> & { description: string }> = {
  [FileStatus.Uploaded]: {
    label: 'Uploaded',
    description:
      "Stored with Ribeye. It hasn't been confirmed in the attribution dataset yet — that check runs hourly.",
    sx: {
      color: Color.Grey,
      background: Color.LightGray,
    },
    icon: <CloudDoneOutlined />,
  },
  [FileStatus.DataReady]: {
    label: 'Data Ready',
    description:
      'Stored with Ribeye and confirmed in the attribution dataset. Ready to use for attribution and retargeting.',
    sx: {
      color: Color.AccentGreen,
      background: Color.SemiTransparentLightGreen,
    },
    icon: <CheckCircleOutline />,
  },
  [FileStatus.Processing]: {
    label: 'Processing',
    description: 'Stored with Ribeye and currently being processed into the attribution dataset.',
    sx: {
      color: Color.AccentYellow,
      background: Color.WarningYellow,
    },
    icon: <HourglassEmptyOutlined />,
  },
  [FileStatus.Failed]: {
    // Deliberately not "Failed": the upload didn't fail, and naming no subject
    // let the reader assume it did.
    label: 'Not processed',
    description:
      "The upload worked and the file is stored with Ribeye, but it didn't reach the attribution dataset within 48 hours. Contact support to have it looked into — there's no need to re-upload.",
    sx: {
      color: Color.AccentRed,
      background: Color.SemiTransparentRed,
    },
    icon: <BlockOutlined />,
  },
};

export const getColumns = () =>
  [
    {
      field: 'name',
      headerName: 'Name',
      getApplyQuickFilterFn: getGridStringQuickFilterFn,
      flex: 1.5,
    },
    {
      field: 'advertiser',
      headerName: 'Advertiser',
      valueGetter: (value: IAdvertiser) => value?.name,
    },
    {
      field: 'clientInfo',
      headerName: 'Client',
      valueGetter: (value: IClient) => value?.name,
    },
    {
      field: 'number_of_rows',
      minWidth: 130,
      maxWidth: 130,
      headerName: '#No of Records',
    },
    {
      field: 'usage_intent',
      minWidth: 110,
      maxWidth: 110,
      headerName: 'Usage Intent',
      sortable: null,
      valueFormatter: (value: UsageIntent) => value || UsageIntent.Attribution, //TODO: remove after adding BE
    },
    {
      field: 'start_date',
      headerName: 'Start date',
      maxWidth: 110,
      valueFormatter: (value: string | Moment) => dateToShortFormat(value),
    },
    {
      field: 'end_date',
      headerName: 'End date',
      maxWidth: 110,
      valueFormatter: (value: string | Moment) => dateToShortFormat(value),
    },
    {
      field: 'created_at',
      headerName: 'Date uploaded',
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams) => (
        <div>
          <div>{dateToShortFormat(value)}</div>
          <div className='text-[#84818A] text-xs font-medium'>{dateToFormat(value, DateFormat.LongTime)}</div>
        </div>
      ),
    },
    {
      field: 'userFullName',
      headerName: 'Uploaded By',
      sortable: false,
      flex: 1.5,
    },
    {
      field: 'status',
      headerName: 'Status',
      // Shown as a header tooltip. The single most useful sentence on the page:
      // it stops "Not processed" being read as a failed upload before anyone
      // has to hover an individual row.
      description:
        'Tracks whether a file has been processed into the attribution dataset — not whether the upload worked. Every file listed here uploaded successfully.',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      flex: 1.3,
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams) => {
        const { description, ...chipProps } = statusChipPropMap[value as FileStatus] ?? {};
        return (
          <Tooltip title={description ?? ''} placement='top' arrow>
            {/* span: Chip is role=presentation, so the tooltip needs a real
                hover target of its own. */}
            <span style={{ display: 'inline-flex' }}>
              <StatusChip {...chipProps} />
            </span>
          </Tooltip>
        );
      },
    },
  ].map(item => ({
    ...item,
    flex: item.flex ?? 1,
    getApplyQuickFilterFn: item.getApplyQuickFilterFn || defaultQuickFilter,
  }));
